import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ShoppingCart, Search, Menu, X, Globe, ChevronDown } from 'lucide-react';
import { useApp } from '../../hooks/useApp';
import { useLanguage } from '../../contexts/LanguageContext';

const LANGUAGES = [
    { code: 'uz', label: "O'zbekcha", short: "O'z" },
    { code: 'ru', label: 'Русский', short: 'Ru' },
    { code: 'en', label: 'English', short: 'En' }
];

const Header = () => {
    const { cart, currentPage, searchQuery, setSearchQuery, setCurrentPage, setSelectedCategory, settings } = useApp();
    const { language, changeLanguage, t } = useLanguage();
    const [showLangDropdown, setShowLangDropdown] = useState(false);
    const langDropdownRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(e) {
            if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
                setShowLangDropdown(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const isNavActive = (page) => {
        if (page === 'shop') return currentPage === 'shop' || currentPage === 'product';
        if (page === 'album') return currentPage === 'album';
        return currentPage === page;
    };

    const currentPageLabel = {
        album: t('album'),
        cart: t('cart'),
        checkout: t('checkout') || "To'lov",
        orders: t('myOrders'),
        profile: t('profile'),
        shipping: t('shipping'),
        returns: t('returns'),
        faq: t('faq'),
        terms: t('terms'),
        privacy: t('privacy')
    }[currentPage];
    const [mobileMenu, setMobileMenu] = useState(false);
    const [mobileMenuExiting, setMobileMenuExiting] = useState(false);

    const closeMobileMenu = useCallback(() => {
        if (!mobileMenu || mobileMenuExiting) return;
        setMobileMenuExiting(true);
        window.setTimeout(() => {
            setMobileMenu(false);
            setMobileMenuExiting(false);
        }, 280);
    }, [mobileMenu, mobileMenuExiting]);

    const openMobileMenu = useCallback(() => {
        setMobileMenuExiting(false);
        setMobileMenu(true);
    }, []);

    return (
        <header className="sticky top-0 bg-white shadow-sm z-50 border-b border-gray-100">
            <div className="container mx-auto px-6 md:px-8 lg:px-12">
                <div className="flex items-center justify-between h-14 md:h-16">
                    {/* Logo */}
                    <div className="flex items-center gap-2 md:gap-3 cursor-pointer" onClick={() => {
                        setCurrentPage('home');
                        setSelectedCategory(null);
                        setSearchQuery('');
                    }}>
                        <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center filter drop-shadow-md hover:scale-105 transition-transform duration-300">
                            <img src={settings.logo_url || "/favicon.svg"} alt={`${settings.site_name || 'Nuur Home'} Logo`} className="w-full h-full object-contain" />
                        </div>
                        <span className="text-[11px] md:text-sm font-serif text-gray-800 tracking-wider uppercase whitespace-nowrap">
                            {settings.site_name || 'Nuur Home'}
                        </span>

                    </div>

                    {/* Current page indicator (sub-pages) */}
                    {currentPageLabel && (
                        <div className="hidden md:flex items-center">
                            <span className="text-xs font-medium text-primary bg-primary/10 px-3 py-1.5 rounded-lg">
                                {currentPageLabel}
                            </span>
                        </div>
                    )}

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-5">
                        {['home', 'shop', 'album', 'about', 'contact'].map((page) => (
                            <button
                                key={page}
                                onClick={() => {
                                    setCurrentPage(page);
                                    if (page === 'home') {
                                        setSelectedCategory(null);
                                        setSearchQuery('');
                                    }
                                }}
                                className={`text-sm font-medium transition-colors capitalize px-2 py-1 rounded-lg ${isNavActive(page)
                                    ? 'text-primary bg-primary/10'
                                    : 'text-gray-700 hover:text-primary hover:bg-gray-50'}`}
                            >
                                {t(page) || page}
                            </button>
                        ))}
                    </nav>

                    {/* Right Actions */}
                    <div className="flex items-center gap-1 md:gap-2">
                        {/* Language Switcher - mobil: keng touch target, aniq label; desktop: globe + til nomi */}
                        <div className="relative" ref={langDropdownRef}>
                            <button
                                onClick={() => setShowLangDropdown(!showLangDropdown)}
                                className="flex items-center gap-2 px-3 py-2.5 md:px-3 md:py-2 text-gray-600 hover:text-primary transition-colors rounded-xl hover:bg-gray-50 active:bg-gray-100 min-h-[44px] md:min-h-0 touch-manipulation"
                                style={{ minWidth: '44px' }}
                            >
                                <Globe className="w-5 h-5 md:w-4 md:h-4 text-primary shrink-0" />
                                <span className="text-sm font-medium md:hidden">
                                    {LANGUAGES.find(l => l.code === language)?.short || language.toUpperCase()}
                                </span>
                                <span className="hidden md:inline text-xs font-medium">{LANGUAGES.find(l => l.code === language)?.label || language.toUpperCase()}</span>
                                <ChevronDown className={`w-4 h-4 md:w-3.5 md:h-3.5 text-gray-400 transition-transform shrink-0 hidden md:block ${showLangDropdown ? 'rotate-180' : ''}`} />
                            </button>
                            {showLangDropdown && (
                                <div className="absolute right-0 mt-2 w-48 min-w-[180px] bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-50 md:w-40 md:min-w-0 origin-top-right ui-dropdown-animate">
                                    {LANGUAGES.map(({ code, label }) => (
                                        <button
                                            key={code}
                                            onClick={() => { changeLanguage(code); setShowLangDropdown(false); }}
                                            className={`w-full text-left px-4 py-3 md:py-2 text-sm hover:bg-gray-50 active:bg-gray-100 transition-colors ${language === code ? 'text-primary font-bold bg-primary/5' : 'text-gray-700'}`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Cart */}
                        <button
                            onClick={() => setCurrentPage('cart')}
                            className="relative p-2 text-gray-700 hover:text-primary transition-colors"
                        >
                            <ShoppingCart className="w-5 h-5 md:w-5 md:h-5" />
                            {cart.length > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 bg-primary text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                                    {cart.length}
                                </span>
                            )}
                        </button>

                        {/* Mobile Menu Toggle */}
                        <button
                            className="md:hidden p-2 text-gray-700 hover:text-primary transition-colors"
                            onClick={openMobileMenu}
                        >
                            <Menu className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* SIMPLE Mobile Menu */}
            {mobileMenu && (
                <div className="fixed inset-0 z-[60] md:hidden">
                    {/* Backdrop */}
                    <div
                        className={`absolute inset-0 bg-black/50 ${mobileMenuExiting ? 'animate-mobile-backdrop-out' : 'animate-mobile-backdrop-in'}`}
                        onClick={closeMobileMenu}
                    />

                    {/* Menu Panel */}
                    <div className={`absolute right-0 top-0 bottom-0 w-[280px] max-w-[85vw] bg-white shadow-2xl ${mobileMenuExiting ? 'animate-mobile-menu-out' : 'animate-mobile-menu-in'}`}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b">
                            <h3 className="text-lg font-bold">Menu</h3>
                            <button onClick={closeMobileMenu} className="p-2">
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Content - Scrollable */}
                        <div className="overflow-y-auto h-[calc(100vh-60px)] p-4">
                            {/* Search */}
                            <div className="mb-4">
                                <div className="relative">
                                    <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Qidirish..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border rounded-lg"
                                    />
                                </div>
                            </div>

                            {/* Navigation */}
                            <div className="space-y-2 mb-6">
                                <p className="text-xs text-gray-500 uppercase px-2 mb-2">Sahifalar</p>
                                {['home', 'shop', 'album', 'about', 'contact'].map((page) => (
                                    <button
                                        key={page}
                                        onClick={() => {
                                            setCurrentPage(page);
                                            if (page === 'home') {
                                                setSelectedCategory(null);
                                                setSearchQuery('');
                                            }
                                            closeMobileMenu();
                                        }}
                                        className={`w-full text-left px-4 py-3 rounded-lg capitalize ${isNavActive(page)
                                            ? 'bg-primary/10 text-primary font-semibold'
                                            : 'hover:bg-gray-100'}`}
                                    >
                                        {t(page) || page}
                                    </button>
                                ))}
                            </div>

                            {/* Til tanlash */}
                            <div className="border-t pt-4 mt-4">
                                <p className="text-xs text-gray-500 uppercase px-2 mb-2 flex items-center gap-2">
                                    <Globe className="w-4 h-4" />
                                    Til
                                </p>
                                <div className="space-y-1">
                                    {LANGUAGES.map(({ code, label }) => (
                                        <button
                                            key={code}
                                            onClick={() => { changeLanguage(code); closeMobileMenu(); }}
                                            className={`w-full text-left px-4 py-3 rounded-lg text-sm ${language === code ? 'bg-primary/10 text-primary font-bold' : 'hover:bg-gray-100 text-gray-700'}`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;